import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useApiClient } from "../../api/ApiContext";
import { ApiError } from "../../api/errors";
import { nfcClockingSchema, nfcClockResultSchema, nfcQueryKeys } from "../../api/nfcSchemas";
import type { NfcClockResult } from "../../api/nfcSchemas";
import { parseInstant } from "../../domain/time";
import type { LocalEvent } from "../../offline/model";
import { useOffline } from "../../offline/OfflineContext";
import { useOnlineStatus } from "../../offline/useOnlineStatus";
import { usePlatform } from "../../platform/PlatformContext";
import type { ClockAction } from "./model/clockActions";

/** Error code of a reading without a SUN message, also used when the tag carries no URL at all. */
export const nfcMessageInvalidCode = "TT-1225";

/** Outcome of a step at a workplace tag. */
export type NfcClockOutcome =
  | { readonly kind: "recorded"; readonly result: NfcClockResult }
  | { readonly kind: "notRead" }
  | { readonly kind: "disabled" }
  | { readonly kind: "refused"; readonly code: string | null };

/** Input of {@link NfcClockControl.clock}. */
export interface NfcClockInput {
  readonly action: ClockAction;
  /** IANA time zone of the employment. */
  readonly timeZoneId: string;
  /** Text of the system sheet (iOS). */
  readonly prompt: string;
}

/** Result of {@link useNfcClock}. */
export interface NfcClockControl {
  /**
   * Whether the clock screen offers steps at a tag: a device with an NFC reader, an employer that offers it (plan and an
   * active workplace tag) and a connection. Without NFC hardware the function stays hidden (T-174).
   */
  readonly available: boolean;
  /**
   * Records a step at a tag: reads the tag after the person pressed the step (`SINV-01`) and sends the reading, which
   * the server checks. The accepted step is added to the local events as confirmed, so the screen shows it at once.
   *
   * @param input - The step.
   * @returns The outcome.
   */
  readonly clock: (input: NfcClockInput) => Promise<NfcClockOutcome>;
  /** Ends a running reading as not read (button „Abbrechen“), or `null` where the platform cannot. */
  readonly cancel: (() => void) | null;
}

/**
 * Clocking at a workplace NFC tag (variant A, T-174, ADR-0143). Only online: a reading is checked by the server at the
 * moment of the step, so nothing waits in the outbox; without a connection the normal steps remain.
 *
 * @param owner - The OIDC subject of the signed-in person, or `null`.
 * @returns The control.
 */
export function useNfcClock(owner: string | null): NfcClockControl {
  const platform = usePlatform();
  const client = useApiClient();
  const offline = useOffline();
  const online = useOnlineStatus();
  const [hardware, setHardware] = useState(false);

  useEffect(() => {
    let active = true;

    if (platform.readNfcTag !== undefined && platform.nfcSupported !== undefined) {
      platform.nfcSupported().then(
        (supported) => {
          if (active) {
            setHardware(supported);
          }
        },
        () => undefined,
      );
    }

    return (): void => {
      active = false;
    };
  }, [platform]);

  const offered = useQuery({
    queryKey: nfcQueryKeys.mine,
    queryFn: ({ signal }) => client.get("/api/v1/me/nfc-clocking", nfcClockingSchema, { signal }),
    enabled: hardware && owner !== null && online,
    staleTime: 5 * 60 * 1000,
  });
  const available = hardware && online && owner !== null && offered.data?.available === true;

  const clock = useCallback(
    async (input: NfcClockInput): Promise<NfcClockOutcome> => {
      if (platform.readNfcTag === undefined || owner === null) {
        const unread: NfcClockOutcome = { kind: "notRead" };
        return unread;
      }

      const read = await platform.readNfcTag(input.prompt);

      if (read.kind !== "read") {
        const unread: NfcClockOutcome = { kind: read.kind === "disabled" ? "disabled" : "notRead" };
        return unread;
      }

      const reading = read.reading;

      if (reading.url === null) {
        const plain: NfcClockOutcome = { kind: "refused", code: nfcMessageInvalidCode };
        return plain;
      }

      const { action } = input;
      const body = {
        id: offline.ids.next(),
        kind: action.kind,
        timeZoneId: input.timeZoneId,
        deviceId: null,
        breakQualification: action.kind === "BreakStart" ? action.breakQualification : null,
        reading: reading.url,
      };

      try {
        const result = await client.post("/api/v1/events:nfc", body, nfcClockResultSchema);
        const nowMs = offline.nowMs();
        const event: LocalEvent = {
          id: result.id,
          ownerSubject: owner,
          kind: action.kind,
          occurredAt: parseInstant(result.occurredAt),
          timeZoneId: input.timeZoneId,
          source: "NfcTag",
          projectId: null,
          clientClockOffsetMs: null,
          breakQualification: body.breakQualification,
          createdAtMs: nowMs,
          syncStatus: "synced",
          syncedAtMs: nowMs,
          attempts: 1,
          error: null,
          hints: result.hints,
          suggestedAction: "None",
        };
        // The server accepted the step: the local copy is a cache, skipped during a sign-out (F-57).
        await offline.store.addSyncedEvent(event);
        const recorded: NfcClockOutcome = { kind: "recorded", result };

        return recorded;
      } catch (error) {
        const refused: NfcClockOutcome = {
          kind: "refused",
          code: error instanceof ApiError ? error.code : null,
        };
        return refused;
      }
    },
    [client, offline, owner, platform],
  );

  const cancel =
    platform.cancelNfcRead === undefined
      ? null
      : (): void => {
          platform.cancelNfcRead?.();
        };
  const control: NfcClockControl = { available, clock, cancel };

  return control;
}
